import React, { useEffect, useState } from "react";
import { CreditCard, Plus, Trash, CheckCircle, Star } from "@phosphor-icons/react";
import { PageLoader, EmptyState, StatusBadge } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { toast } from "sonner";
import api, { apiErr } from "../../lib/api";

export default function PaymentMethods() {
  const [pms, setPms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("card");
  const [form, setForm] = useState({ card_number: "", card_holder: "", expiry: "", upi_id: "" });

  const load = () => api.get("/account/payment-methods").then((r) => setPms(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const add = async () => {
    try {
      const body = type === "card"
        ? { type, card_number: form.card_number, card_holder: form.card_holder, expiry: form.expiry }
        : { type, upi_id: form.upi_id };
      await api.post("/account/payment-methods", body);
      toast.success("Payment method added. Verify to use it.");
      setOpen(false); setForm({ card_number: "", card_holder: "", expiry: "", upi_id: "" }); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const act = async (id, action) => {
    try {
      if (action === "delete") await api.delete(`/account/payment-methods/${id}`);
      else await api.post(`/account/payment-methods/${id}/${action}`);
      toast.success("Updated"); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 md:px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline mb-1">Wallet</div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">Payment Methods</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="add-pm-btn"><Plus className="mr-1.5" /> Add</Button></DialogTrigger>
          <DialogContent data-testid="add-pm-dialog">
            <DialogHeader><DialogTitle>Add payment method</DialogTitle></DialogHeader>
            <Tabs value={type} onValueChange={setType}>
              <TabsList className="w-full"><TabsTrigger value="card" className="flex-1">Card</TabsTrigger><TabsTrigger value="upi" className="flex-1">UPI</TabsTrigger></TabsList>
              <TabsContent value="card" className="space-y-3 pt-3">
                <div><Label>Card number</Label><Input data-testid="card-number" value={form.card_number} onChange={(e) => setForm({ ...form, card_number: e.target.value })} placeholder="4111 1111 1111 4582" className="mt-1.5" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Card holder</Label><Input value={form.card_holder} onChange={(e) => setForm({ ...form, card_holder: e.target.value })} className="mt-1.5" /></div>
                  <div><Label>Expiry</Label><Input value={form.expiry} onChange={(e) => setForm({ ...form, expiry: e.target.value })} placeholder="MM/YY" className="mt-1.5" /></div>
                </div>
              </TabsContent>
              <TabsContent value="upi" className="space-y-3 pt-3">
                <div><Label>UPI ID</Label><Input data-testid="upi-id" value={form.upi_id} onChange={(e) => setForm({ ...form, upi_id: e.target.value })} placeholder="name@bank" className="mt-1.5" /></div>
              </TabsContent>
            </Tabs>
            <p className="text-xs text-muted-foreground">We never store your full card or CVV — only a secure token and masked details.</p>
            <DialogFooter><Button onClick={add} data-testid="save-pm" className="w-full rounded-full">Add securely</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? <PageLoader /> : pms.length === 0 ? (
        <EmptyState icon={CreditCard} title="No payment methods" subtitle="Add a tokenized card or UPI ID for faster checkout." testid="no-pms" />
      ) : (
        <div className="space-y-3">
          {pms.map((p) => (
            <div key={p.id} data-testid={`pm-${p.id}`} className="flex items-center justify-between p-4 rounded-xl border border-border bg-card">
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 grid place-items-center rounded-lg bg-muted"><CreditCard size={20} /></span>
                <div>
                  <div className="font-medium flex items-center gap-2">{p.label} {p.is_default && <span className="text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary">Default</span>}</div>
                  <div className="mt-0.5"><StatusBadge status={p.status} /></div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {p.status !== "verified" && <Button variant="ghost" size="sm" onClick={() => act(p.id, "verify")} data-testid={`verify-${p.id}`}><CheckCircle size={16} className="mr-1" /> Verify</Button>}
                {!p.is_default && <Button variant="ghost" size="sm" onClick={() => act(p.id, "default")} data-testid={`default-${p.id}`}><Star size={16} /></Button>}
                <Button variant="ghost" size="sm" onClick={() => act(p.id, "delete")} data-testid={`remove-${p.id}`} className="text-destructive"><Trash size={16} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
